import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { Typography, Box } from "@material-ui/core";
import { ErrorOutline } from "@material-ui/icons";

// 🎯 Componentes de mídia específicos otimizados
import {
  ImageMedia,
  VideoMedia,
  AudioMedia,
  FileMedia,
  LocationMedia,
  ContactMedia,
  UnsupportedMedia
} from "./MediaComponents";

// 🎯 Hook de estado de mídia
import { useMediaState } from "../hooks/useMediaState";

// 🎯 Constantes
import { MESSAGE_MEDIA_TYPES } from "../constants/messageConstants";

// 🎨 Estilos
import { useMediaStyles } from "../styles/mediaStyles";

/**
 * 🎬 MediaRenderer - Componente avançado para renderização de mídias
 * 
 * Sistema completo de renderização de mídias com:
 * - Design moderno e responsivo inspirado no WhatsApp 2.0
 * - Estados de carregamento e erro
 * - Otimizações de performance
 * - Acessibilidade aprimorada
 * - Suporte completo a todos os tipos de mídia
 * - Contextos variados (normal, quoted, preview)
 * 
 * @param {Object} message - Mensagem com dados de mídia
 * @param {Function} onError - Callback para tratamento de erros
 * @param {Function} onLoad - Callback para quando a mídia carrega
 * @param {string} variant - Contexto de renderização (normal, quoted, preview)
 * @param {string} size - Tamanho da mídia (small, medium, large)
 * @returns {JSX.Element|null} - Componente de mídia renderizado ou null
 */
const MediaRenderer = ({ 
  message, 
  onError, 
  onLoad, 
  variant = "normal", 
  size = "medium",
  buildImageSequence,
}) => {
  const classes = useMediaStyles();
  const mediaState = useMediaState(message);
  // 🎯 Handler de erro centralizado
  const handleError = useCallback((error) => {
    mediaState.actions.onError(error);
    if (onError) {
      onError(error);
    }
  }, [mediaState.actions, onError]);

  // 🎯 Handler de carregamento centralizado
  const handleLoad = useCallback(() => {
    mediaState.actions.onLoadEnd();
    if (onLoad) {
      onLoad();
    }
  }, [mediaState.actions, onLoad]);

  // 🔍 Validação inicial
  if (!mediaState.isValid) {
    return null;
  }

  // 🚫 Tratamento de erro geral
  if (mediaState.hasError) {
    return (
      <Box className={classes.errorContainer}>
        <ErrorOutline style={{ marginRight: 8 }} />
        <Typography variant="body2">
          Erro ao carregar mídia
        </Typography>
      </Box>
    );
  }

  // 🎯 Renderização baseada no tipo de mídia
  const renderMediaComponent = () => {
    switch (mediaState.type) {
      case MESSAGE_MEDIA_TYPES.IMAGE:
        return (
          <ImageMedia 
            message={message}
            onError={handleError}
            onLoad={handleLoad}
            variant={variant}
            size={size}
            buildImageSequence={buildImageSequence}
          />
        );

      case MESSAGE_MEDIA_TYPES.VIDEO:
        return (
          <VideoMedia 
            message={message}
            onError={handleError}
            onLoad={handleLoad}
            variant={variant}
            size={size}
          />
        );

      case MESSAGE_MEDIA_TYPES.AUDIO:
        return (
          <AudioMedia 
            message={message}
            onError={handleError}
            onLoad={handleLoad}
          />
        );

      case MESSAGE_MEDIA_TYPES.APPLICATION:
        return (
          <FileMedia 
            message={message}
            onError={handleError}
            onLoad={handleLoad}
          />
        );

      case MESSAGE_MEDIA_TYPES.LOCATION:
        return (
          <LocationMedia 
            message={message}
            onError={handleError}
            onLoad={handleLoad}
          />
        );

      case MESSAGE_MEDIA_TYPES.CONTACT:
        return (
          <ContactMedia 
            message={message}
            onError={handleError}
            onLoad={handleLoad}
            variant={variant}
            size={size}
          />
        );

      default:
        if (message?.mediaUrl) {
          return (
            <FileMedia
              message={message}
              onError={handleError}
              onLoad={handleLoad}
            />
          );
        }

        return (
          <UnsupportedMedia 
            mediaType={mediaState.type} 
          />
        );
    }
  };

  return renderMediaComponent();
};

MediaRenderer.propTypes = {
  message: PropTypes.shape({
    mediaType: PropTypes.string,
    mediaUrl: PropTypes.string,
    body: PropTypes.string,
    filename: PropTypes.string,
    originalName: PropTypes.string,
    fileSize: PropTypes.number,
    duration: PropTypes.string,
    thumbnailUrl: PropTypes.string,
  }).isRequired,
  onError: PropTypes.func,
  onLoad: PropTypes.func,
  variant: PropTypes.oneOf(['normal', 'quoted', 'preview']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  buildImageSequence: PropTypes.func,
};

MediaRenderer.defaultProps = {
  onError: null,
  onLoad: null,
  variant: 'normal',
  size: 'medium',
  buildImageSequence: undefined,
};

export default MediaRenderer;
